/*
  Vue3 Modal Helper based on Element Plus
  提供快捷的 create/createStatic 方法，第二个参数为组件参数，第三个参数仅支持控制宽度
*/
import { createVNode, render } from 'vue';
import { ElDialog, ElConfigProvider } from 'element-plus'; // 导入 ConfigProvider
import zhCn from 'element-plus/es/locale/lang/zh-cn'; // 导入中文语言包
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
const containers = [];
dayjs.locale('zh-cn');
let globalApp = null; // 新增全局变量
export function setGlobalApp(app) {
    globalApp = app;
}
/**
 * 核心方法：动态创建并挂载 ElDialog
 * @param Comp - 要渲染的弹窗组件
 * @param params - 传入组件的 props 参数
 * @param options - 对话框配置，目前仅支持 width
 * @param maskClosable - 点击蒙层是否可关闭
 */
/**
 * 手机端(<=480px)对话框宽度自适应，避免固定像素宽度超出屏宽产生横向滚动
 * 数字或 '400px' 等固定值 -> min(原值, calc(100vw - 32px))；空值 -> calc(100vw - 32px)
 */
function clampDialogWidth(width) {
    const isMobile = document.documentElement.clientWidth <= 480 ||
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile)
        return width === undefined || width === null ? undefined : String(width);
    if (typeof width === 'number')
        return `min(${width}px, calc(100vw - 32px))`;
    const str = width || '';
    if (/^\d+(\.\d+)?px$/i.test(str) || /^\d+(\.\d+)?$/.test(str)) {
        return `min(${str}${/^\d+$/.test(str) ? 'px' : ''}, calc(100vw - 32px))`;
    }
    return str || 'calc(100vw - 32px)';
}
function createModal(Comp, params = {}, options = {}, maskClosable) {
    return new Promise((resolve) => {
        const container = document.createElement('div');
        document.body.appendChild(container);
        containers.push(container);
        let isClosing = false;
        const onClose = (result) => {
            if (isClosing)
                return;
            isClosing = true;
            try {
                render(null, container);
                if (container && container.parentNode) {
                    container.parentNode.removeChild(container);
                }
                const idx = containers.indexOf(container);
                if (idx !== -1) {
                    containers.splice(idx, 1);
                }
                if (result !== undefined) {
                    resolve(result);
                }
            }
            catch (error) {
                console.error('关闭弹窗时发生错误:', error);
            }
        };
        // 创建强制中文配置的弹窗内容
        const contentVNode = createVNode(ElConfigProvider, // 使用 ConfigProvider 包裹内容
        {
            locale: zhCn, // 强制设置为中文
        }, {
            default: () => createVNode(Comp, {
                ...params,
                close: onClose,
            }),
        });
        const dialogVNode = createVNode(ElDialog, {
            modelValue: true,
            'onUpdate:modelValue': (val) => {
                if (!val)
                    onClose();
            },
            width: clampDialogWidth(options.width) || '500px',
            top: options.top || '15vh',
            closeOnClickModal: maskClosable,
            destroyOnClose: true,
            title: options.title,
        }, {
            default: () => contentVNode, // 将 ConfigProvider 包裹的内容放入弹窗
        });
        if (globalApp) {
            dialogVNode.appContext = globalApp._context;
        }
        render(dialogVNode, container);
    });
}
/**
 * 打开模态框，点击蒙层可关闭
 */
export function create(Comp, params, options) {
    return createModal(Comp, params, options || {}, true);
}
/**
 * 打开模态框，点击蒙层不可关闭
 */
export function createStatic(Comp, params, options) {
    return createModal(Comp, params, options || {}, false);
}
export function closeAllModals() {
    for (const container of containers) {
        render(null, container);
        if (container.parentNode) {
            container.parentNode.removeChild(container);
        }
    }
    containers.length = 0;
}
